import {
  Briefcase,
  Calculator,
  CalendarDays,
  Check,
  Clock3,
  Coins,
  Gift,
  LogOut,
  RefreshCw,
  Settings2,
  ShieldAlert,
  UserCheck2,
  UserRound,
  Users,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SystemFeatureFlags } from "./types";
import { TabType, ModuleType } from "./types";
import { useLanguage, LanguageToggle } from "@/lib/i18n";
import { FontSizeToggle } from "./FontSizeToggle";


interface HomeHeaderProps {
  currentDisplayName: string;
  savedName: string;
  liffState: "loading" | "login" | "ready" | "error";
  liffError: string;
  isManager: boolean;
  executiveEntry: boolean;
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
  inputName: string;
  setInputName: (name: string) => void;
  candidateNames: string[];
  handleSaveFirstTimeName: (name: string) => void;
  handleClearSavedUser: () => void;
  moduleMascot: {
    eyebrow: string;
    title: string;
    detail: string;
    image: string;
  } | null;
  showFirstTimeModal: boolean;
  entryModule: ModuleType | null;
  featureFlags?: SystemFeatureFlags;
}

export function HomeHeader({
  currentDisplayName,
  savedName,
  liffState,
  liffError,
  isManager,
  executiveEntry,
  activeTab,
  setActiveTab,
  inputName,
  setInputName,
  candidateNames,
  handleSaveFirstTimeName,
  handleClearSavedUser,
  moduleMascot,
  showFirstTimeModal,
  entryModule,
  featureFlags,
}: HomeHeaderProps) {
  const { t } = useLanguage();
  return (
    <>
      {/* Top Main Navigation Header */}
      <header className="main-nav-header sticky top-0 z-40 bg-white/80 backdrop-blur-lg border-b border-slate-200/80 mb-2 sm:mb-3 shadow-sm">
        <div className={`reference-header-inner dashboard-container mx-auto px-2 sm:px-5 py-2 sm:py-3 flex items-center justify-between gap-1.5 sm:gap-3 ${executiveEntry ? "dashboard-container-admin" : "dashboard-container-employee"}`}>
          {/* Logo & System Brand */}
          <div className="reference-brand flex items-center gap-1.5 sm:gap-2.5 min-w-0 shrink">
            <img
              src="/demo-logo.png"
              alt="Demo Check-In"
              className="brand-logo h-8 sm:h-12 w-auto object-contain shrink-0"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <div className="reference-brand-copy min-w-0 hidden min-[400px]:block">
              <div className="flex items-center gap-1.5">
                <span className="font-black text-slate-900 tracking-tight text-sm sm:text-lg leading-none">
                  Demo
                </span>
                <span className="rounded-full bg-[#ebf6ee] px-2 py-0.5 text-[9px] sm:text-[10px] font-black text-[#1b6b44] border border-[#cbe4d2] uppercase tracking-wide">
                  Check-In
                </span>
              </div>
              <p className="text-[11px] font-semibold text-slate-500 truncate hidden sm:block">
                {t.appSubtitle}
              </p>
            </div>
          </div>

          {/* User Status / Action Button & Language Switcher */}
          <div className="reference-header-actions flex items-center gap-1.5 sm:gap-2 shrink-0">
            <FontSizeToggle className="reference-font-toggle" />
            {!executiveEntry && <LanguageToggle className="reference-language-toggle" />}

            {savedName && (
              <div className="flex items-center gap-1 sm:gap-2 rounded-full bg-[#ebf6ee] pl-2.5 sm:pl-3.5 pr-1 sm:pr-1.5 py-0.5 sm:py-1 border border-[#cbe4d2] text-xs font-bold text-[#1b6b44] shadow-xs max-w-[110px] sm:max-w-none">
                <span className="truncate max-w-[60px] sm:max-w-[160px]">
                  {savedName}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 sm:h-6 sm:w-6 rounded-full hover:bg-emerald-100 text-slate-500 hover:text-rose-600 shrink-0"
                  onClick={handleClearSavedUser}
                  title={t.changeUserTooltip}
                >
                  <LogOut size={12} />
                </Button>
              </div>
            )}

            {executiveEntry && isManager && (
              <span className="hidden sm:inline-flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-xs font-black text-emerald-800 border border-emerald-300 shadow-xs">
                <Zap size={13} className="fill-emerald-600 text-emerald-700" />{" "}
                {t.userRoleExecutive}
              </span>
            )}
          </div>
        </div>
      </header>

      <main className={`dashboard-container mx-auto px-3 sm:px-5 pb-8 sm:pb-12 ${executiveEntry ? "dashboard-container-admin" : "dashboard-container-employee"}`}>
        {/* Onboarding Identity Setup (Shown if no saved name & not executive entry) */}
        {!savedName && !currentDisplayName && !executiveEntry && (
          <Card className="glass-panel mb-6 rounded-3xl border-[#cbe4d2] shadow-xl overflow-hidden relative">
            <div className="h-2 bg-gradient-to-r from-[#1b6b44] via-[#2e8b57] to-[#86efac]" />
            <CardContent className="p-5 sm:p-8">
              {/* Language Selector Banner prominently on top of onboarding card */}
              <div className="flex items-center justify-between p-3.5 mb-5 rounded-2xl bg-[#ebf6ee] border border-[#cbe4d2]">
                <div className="flex items-center gap-2">
                  <span className="text-xl">🌐</span>
                  <div>
                    <p className="text-xs font-black text-slate-800 uppercase tracking-wider">{t.selectLanguageTitle}</p>
                    <p className="text-[11px] font-semibold text-slate-500">เลือกภาษาที่ต้องการใช้งาน</p>
                  </div>
                </div>
                <LanguageToggle />
              </div>

              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#ebf6ee] text-[#1b6b44]">
                  <UserRound size={30} />
                </div>
                <div>
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#ebf6ee] px-3 py-1 text-xs font-black text-[#1b6b44] border border-[#cbe4d2] mb-1.5">
                    {t.welcomeBadge}
                  </span>
                  <h2 className="text-2xl sm:text-3xl font-black text-slate-900">
                    {t.onboardingTitle}
                  </h2>
                  <p className="text-sm sm:text-base font-semibold text-slate-600 mt-1.5">
                    {t.onboardingSub}
                  </p>
                </div>
              </div>

              <div className="mt-6 space-y-4 pt-4 border-t border-slate-100">
                <div>
                  <label className="block text-sm font-black uppercase text-slate-700 mb-2">
                    {t.selectFromRoster}
                  </label>
                  <select
                    className="w-full rounded-2xl border-2 border-[#d9e3dc] bg-white px-4 py-3.5 text-base font-bold text-slate-900 shadow-xs focus:border-[#1b6b44] focus:ring-2 focus:ring-[#1b6b44]/20 outline-none"
                    value={inputName}
                    onChange={(e) => setInputName(e.target.value)}
                  >
                    <option value="">{t.selectNamePlaceholder}</option>
                    {candidateNames.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="relative flex items-center justify-center">
                  <div className="border-t border-slate-200 w-full" />
                  <span className="bg-white px-3 text-xs font-bold text-slate-400 uppercase absolute">
                    {t.orTypeOwnName}
                  </span>
                </div>

                <div>
                  <input
                    type="text"
                    placeholder={t.typeOwnPlaceholder}
                    className="w-full rounded-2xl border-2 border-[#d9e3dc] bg-white px-4 py-3.5 text-base font-bold text-slate-900 shadow-xs focus:border-[#1b6b44] focus:ring-2 focus:ring-[#1b6b44]/20 outline-none"
                    value={inputName}
                    onChange={(e) => setInputName(e.target.value)}
                  />
                </div>

                <Button
                  className="btn-primary-teal h-14 w-full rounded-full text-base sm:text-lg font-black shadow-lg shadow-[#1b6b44]/25 mt-2"
                  onClick={() => handleSaveFirstTimeName(inputName)}
                  disabled={!inputName.trim()}
                >
                  <Check className="mr-2" size={24} strokeWidth={3} />
                  {t.loginAndStartBtn}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {executiveEntry && (liffState === "loading" || liffState === "login") && (
          <Card className="glass-panel mb-6 rounded-3xl border-[#cbe4d2] p-8 text-center shadow-lg">
            <RefreshCw className="mx-auto mb-3 animate-spin text-[#1b6b44]" size={30} />
            <h2 className="text-lg font-black text-slate-900">กำลังยืนยันสิทธิ์ผู้ดูแลระบบ</h2>
            <p className="mt-1 text-sm font-semibold text-slate-500">ระบบจะเปิดแดชบอร์ดให้อัตโนมัติ</p>
          </Card>
        )}

        {/* Unauthorized admin state: never offer a client-side bypass button. */}
        {executiveEntry && liffState !== "loading" && liffState !== "login" && !isManager && (
          <Card className="glass-panel mb-6 rounded-[2.5rem] border-rose-200/80 shadow-2xl overflow-hidden relative">
            <div className="h-2.5 bg-gradient-to-r from-slate-900 via-rose-500 to-amber-500" />
            <CardContent className="p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-tr from-slate-900 to-slate-800 text-amber-400 shadow-lg">
                  <ShieldAlert size={32} />
                </div>
                <div>
                  <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-3 py-1 text-xs font-black text-rose-800 mb-1">
                    EXECUTIVE & HR PORTAL
                  </span>
                  <h2 className="text-2xl sm:text-3xl font-black text-slate-900">
                    ไม่มีสิทธิ์เข้าถึงแดชบอร์ดผู้บริหาร
                  </h2>
                  <p className="text-sm sm:text-base font-semibold text-slate-600 mt-1">
                    บัญชี LINE นี้ไม่ได้อยู่ในรายชื่อผู้ดูแลระบบ กรุณาติดต่อผู้ดูแลเพื่อเพิ่มสิทธิ์
                    {liffError ? ` (${liffError})` : ""}
                  </p>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-slate-100 flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  className="h-14 px-6 rounded-full text-base font-bold text-slate-600 border-slate-200 hover:bg-slate-50"
                  onClick={() => {
                    window.location.href = "/";
                  }}
                >
                  ไปยังหน้าลงเวลาพนักงานทั่วไป
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tab Navigation Menu - CleanNest Segmented Pill Style */}
        {(!executiveEntry || isManager) && (
          <div className={`employee-tabs ${executiveEntry ? "admin-tabs" : "staff-tabs reference-staff-tabs"} mb-4 sm:mb-6 grid gap-1.5 p-1.5 rounded-full bg-[#ebf3ed] border border-[#dce9df]`}>
            {executiveEntry ? (
              <>
                <button
                  aria-label={t.tabLiveBoard}
                  title={t.tabLiveBoard}
                  onClick={() => { window.history.replaceState({}, "", "/admin/?module=live-board"); setActiveTab("live-board"); }}
                  className={`tab-pill flex items-center gap-2 rounded-full px-4.5 py-3 text-sm sm:text-base font-extrabold whitespace-nowrap transition-all ${
                    activeTab === "live-board"
                      ? "active text-white"
                      : "text-slate-600 hover:text-[#1b6b44] hover:bg-white/60"
                  }`}
                >
                  <Users
                    size={18}
                    className={
                      activeTab === "live-board"
                        ? "text-white"
                        : "text-slate-400"
                    }
                  />
                  <span className="tab-label">{t.tabLiveBoard}</span>
                </button>

                <button
                  aria-label={t.tabPayroll}
                  title={t.tabPayroll}
                  onClick={() => { window.history.replaceState({}, "", "/admin/?module=payroll"); setActiveTab("payroll"); }}
                  className={`tab-pill flex items-center gap-2 rounded-full px-4.5 py-3 text-sm sm:text-base font-extrabold whitespace-nowrap transition-all ${
                    activeTab === "payroll"
                      ? "active text-white"
                      : "text-slate-600 hover:text-[#1b6b44] hover:bg-white/60"
                  }`}
                >
                  <Calculator
                    size={18}
                    className={
                      activeTab === "payroll"
                        ? "text-white"
                        : "text-slate-400"
                    }
                  />
                  <span className="tab-label">{t.tabPayroll}</span>
                </button>

                {featureFlags?.enableSalaryAdvance !== false && <button
                  aria-label={t.tabAdvanceApprovals}
                  title={t.tabAdvanceApprovals}
                  onClick={() => { window.history.replaceState({}, "", "/admin/?module=advance-approvals"); setActiveTab("advance-approvals"); }}
                  className={`tab-pill flex items-center gap-2 rounded-full px-4.5 py-3 text-sm sm:text-base font-extrabold whitespace-nowrap transition-all ${
                    activeTab === "advance-approvals"
                      ? "active text-white"
                      : "text-slate-600 hover:text-[#1b6b44] hover:bg-white/60"
                  }`}
                >
                  <Coins
                    size={18}
                    className={
                      activeTab === "advance-approvals"
                        ? "text-white"
                        : "text-slate-400"
                    }
                  />
                  <span className="tab-label">{t.tabAdvanceApprovals}</span>
                </button>}

                {Boolean(featureFlags?.enableLeaveSystem) && (
                  <button
                    aria-label={t.tabLeaveApprovals}
                    title={t.tabLeaveApprovals}
                    onClick={() => { window.history.replaceState({}, "", "/admin/?module=leave-approvals"); setActiveTab("leave-approvals"); }}
                    className={`tab-pill flex items-center gap-2 rounded-full px-4.5 py-3 text-sm sm:text-base font-extrabold whitespace-nowrap transition-all ${
                      activeTab === "leave-approvals"
                        ? "active text-white"
                        : "text-slate-600 hover:text-[#1b6b44] hover:bg-white/60"
                    }`}
                  >
                    <UserCheck2
                      size={18}
                      className={
                        activeTab === "leave-approvals"
                          ? "text-white"
                          : "text-slate-400"
                      }
                    />{" "}
                    <span className="tab-label">{t.tabLeaveApprovals}</span>
                  </button>
                )}

                <button
                  aria-label={t.tabTeam}
                  title={t.tabTeam}
                  onClick={() => { window.history.replaceState({}, "", "/admin/?module=team-directory"); setActiveTab("team-directory"); }}
                  className={`tab-pill flex items-center gap-2 rounded-full px-4.5 py-3 text-sm sm:text-base font-extrabold whitespace-nowrap transition-all ${
                    activeTab === "team-directory"
                      ? "active text-white"
                      : "text-slate-600 hover:text-[#1b6b44] hover:bg-white/60"
                  }`}
                >
                  <Users
                    size={18}
                    className={
                      activeTab === "team-directory"
                        ? "text-white"
                        : "text-slate-400"
                    }
                  />
                  <span className="tab-label">{t.tabTeam}</span>
                </button>

                <button
                  aria-label={t.tabSettings}
                  title={t.tabSettings}
                  onClick={() => { window.history.replaceState({}, "", "/admin/?module=hr-settings"); setActiveTab("hr-settings"); }}
                  className={`tab-pill flex items-center gap-2 rounded-full px-4.5 py-3 text-sm sm:text-base font-extrabold whitespace-nowrap transition-all ${
                    activeTab === "hr-settings"
                      ? "active text-white"
                      : "text-slate-600 hover:text-[#1b6b44] hover:bg-white/60"
                  }`}
                >
                  <Settings2
                    size={18}
                    className={
                      activeTab === "hr-settings"
                        ? "text-white"
                        : "text-slate-400"
                    }
                  />
                  <span className="tab-label">{t.tabSettings}</span>
                </button>

                <button
                  aria-label="สั่งงาน / OT"
                  title="สั่งงาน / OT"
                  onClick={() => { window.history.replaceState({}, "", "/admin/?module=tasks"); setActiveTab("tasks"); }}
                  className={`tab-pill flex items-center gap-2 rounded-full px-4.5 py-3 text-sm sm:text-base font-extrabold whitespace-nowrap transition-all ${
                    activeTab === "tasks"
                      ? "active text-white"
                      : "text-slate-600 hover:text-[#1b6b44] hover:bg-white/60"
                  }`}
                >
                  <Zap
                    size={18}
                    className={
                      activeTab === "tasks"
                        ? "text-white"
                        : "text-slate-400"
                    }
                  />
                  <span className="tab-label">สั่งงาน / OT</span>
                </button>

                <button
                  aria-label="สวัสดิการ"
                  title="สวัสดิการ"
                  onClick={() => { window.history.replaceState({}, "", "/admin/?module=welfare"); setActiveTab("welfare"); }}
                  className={`tab-pill flex items-center gap-2 rounded-full px-4.5 py-3 text-sm sm:text-base font-extrabold whitespace-nowrap transition-all ${
                    activeTab === "welfare"
                      ? "active text-white"
                      : "text-slate-600 hover:text-[#1b6b44] hover:bg-white/60"
                  }`}
                >
                  <Gift
                    size={18}
                    className={
                      activeTab === "welfare"
                        ? "text-white"
                        : "text-slate-400"
                    }
                  />
                  <span className="tab-label">สวัสดิการ</span>
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => { window.history.pushState({}, "", "/?module=attendance"); setActiveTab("attendance"); }}
                  className={`tab-pill flex items-center gap-2 rounded-full px-4.5 py-3 text-sm sm:text-base font-extrabold whitespace-nowrap transition-all ${
                    activeTab === "attendance"
                      ? "active text-white"
                      : "text-slate-600 hover:text-[#1b6b44] hover:bg-white/60"
                  }`}
                >
                  <Clock3
                    size={18}
                    className={
                      activeTab === "attendance"
                        ? "text-white"
                        : "text-slate-400"
                    }
                  />
                  <span className="tab-label">{t.tabAttendance}</span>
                </button>

                {featureFlags?.enableSalaryAdvance !== false && <button
                  onClick={() => { window.history.pushState({}, "", "/?module=salary-advance"); setActiveTab("salary-advance"); }}
                  className={`tab-pill flex items-center gap-2 rounded-full px-4.5 py-3 text-sm sm:text-base font-extrabold whitespace-nowrap transition-all ${
                    activeTab === "salary-advance"
                      ? "active text-white"
                      : "text-slate-600 hover:text-[#1b6b44] hover:bg-white/60"
                  }`}
                >
                  <Coins
                    size={18}
                    className={
                      activeTab === "salary-advance"
                        ? "text-white"
                        : "text-slate-400"
                    }
                  />
                  <span className="tab-label">{t.tabAdvance}</span>
                </button>}

                {Boolean(featureFlags?.enableLeaveSystem) && (
                  <button
                    onClick={() => { window.history.pushState({}, "", "/?module=leave"); setActiveTab("leave"); }}
                    className={`tab-pill flex items-center gap-2 rounded-full px-4.5 py-3 text-sm sm:text-base font-extrabold whitespace-nowrap transition-all ${
                      activeTab === "leave"
                        ? "active text-white"
                        : "text-slate-600 hover:text-[#1b6b44] hover:bg-white/60"
                    }`}
                  >
                    <Briefcase
                      size={18}
                      className={
                        activeTab === "leave"
                          ? "text-white"
                          : "text-slate-400"
                      }
                    />{" "}
                    <span className="tab-label">{t.tabLeave}</span>
                  </button>
                )}

                <button
                  onClick={() => { window.history.pushState({}, "", "/?module=tasks"); setActiveTab("tasks"); }}
                  className={`tab-pill flex items-center gap-2 rounded-full px-4.5 py-3 text-sm sm:text-base font-extrabold whitespace-nowrap transition-all ${
                    activeTab === "tasks"
                      ? "active text-white"
                      : "text-slate-600 hover:text-[#1b6b44] hover:bg-white/60"
                  }`}
                >
                  <Zap
                    size={18}
                    className={
                      activeTab === "tasks"
                        ? "text-white"
                        : "text-slate-400"
                    }
                  />
                  <span className="tab-label">{t.tabTasks}</span>
                </button>

                <button
                  onClick={() => { window.history.pushState({}, "", "/?module=welfare"); setActiveTab("welfare"); }}
                  className={`tab-pill flex items-center gap-2 rounded-full px-4.5 py-3 text-sm sm:text-base font-extrabold whitespace-nowrap transition-all ${
                    activeTab === "welfare"
                      ? "active text-white"
                      : "text-slate-600 hover:text-[#1b6b44] hover:bg-white/60"
                  }`}
                >
                  <Gift
                    size={18}
                    className={
                      activeTab === "welfare"
                        ? "text-white"
                        : "text-slate-400"
                    }
                  />
                  <span className="tab-label">{t.tabWelfare}</span>
                </button>
              </>
            )}
          </div>
        )}

        {((executiveEntry && isManager) || (!executiveEntry && currentDisplayName)) && moduleMascot && (
          <section
            className="module-hero mb-3 sm:mb-5"
            aria-label={moduleMascot.title}
          >
            <div className="module-hero-copy">
              <p>{moduleMascot.eyebrow}</p>
              <h2>{moduleMascot.title}</h2>
              <span>{moduleMascot.detail}</span>
            </div>
            <img src={moduleMascot.image} alt="Demo module assistant" />
          </section>
        )}
      </main>
    </>
  );
}
